import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
} from '@mui/material';
import {
  addIgnoredLocatorAsync,
  compareScreenDriftAsync,
  fetchScreenDriftGroupedAsync,
  selectIgnoreList,
  selectScreenDrift,
  setScreenDriftShowIgnored,
  setScreenDriftShowTestImpactOnly,
} from '../../../redux/locatorDrift/locatorDriftSlice';
import { driftCategories } from '../locatorDriftConstants';
import EmptyState from '../shared/EmptyState';
import DriftSummary from './DriftSummary';
import DriftTable from './DriftTable';
import VersionSelect from '../shared/VersionSelect';
import { isLocatorIgnored } from '../../../utilities/locatorDrift/locatorFormatters';
import { screenDriftTabStyles } from './screenDriftStyles';

export default function ScreenDriftTab() {
  const dispatch = useDispatch();
  const screenDrift = useSelector(selectScreenDrift);
  const ignoreList = useSelector(selectIgnoreList);
  const [groupIndex, setGroupIndex] = useState('');
  const [versionA, setVersionA] = useState('');
  const [versionB, setVersionB] = useState('');
  const [expandedIndex, setExpandedIndex] = useState(null);
  const grouped = screenDrift.grouped;
  const selectedGroup = groupIndex !== '' ? grouped[Number(groupIndex)] : null;
  const versions = selectedGroup?.versions ? Object.keys(selectedGroup.versions) : [];
  const comparison = screenDrift.comparison;
  const items = comparison?.items || [];

  useEffect(() => {
    dispatch(fetchScreenDriftGroupedAsync());
  }, [dispatch]);

  const filteredCategories = driftCategories.map((category) => {
    let categoryItems = items.filter(
      (item) => (item.status || item.drift || '').toUpperCase() === category.key,
    );
    if (screenDrift.showTestImpactOnly) {
      categoryItems = categoryItems.filter(
        (item) => item.in_both !== false || ['REGRESSION', 'REMOVED'].includes(category.key),
      );
    }
    if (!screenDrift.showIgnored) {
      categoryItems = categoryItems.filter(
        (item) =>
          !isLocatorIgnored(
            ignoreList,
            item.locator_by || item.by,
            item.locator_value || item.value,
          ),
      );
    }
    return { ...category, items: categoryItems };
  });

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={screenDriftTabStyles.controlsPaper}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Scenario</InputLabel>
              <Select
                label="Scenario"
                value={groupIndex}
                onChange={(event) => {
                  setGroupIndex(event.target.value);
                  setVersionA('');
                  setVersionB('');
                }}
              >
                {grouped.map((group, index) => (
                  <MenuItem
                    key={`${group.scenario}-${group.platform}-${index}`}
                    value={String(index)}
                  >
                    {group.scenario} [{group.platform}]
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <VersionSelect
              label="Version A"
              versions={versions}
              value={versionA}
              onChange={setVersionA}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <VersionSelect
              label="Version B"
              versions={versions}
              value={versionB}
              onChange={setVersionB}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              disabled={
                !selectedGroup ||
                !versionA ||
                !versionB ||
                versionA === versionB ||
                screenDrift.comparing
              }
              onClick={() =>
                dispatch(
                  compareScreenDriftAsync({
                    scenario: selectedGroup.scenario,
                    platform: selectedGroup.platform,
                    version_a: versionA,
                    version_b: versionB,
                  }),
                )
              }
              sx={screenDriftTabStyles.compareButton}
            >
              {screenDrift.comparing ? <CircularProgress size={20} /> : 'Compare'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {screenDrift.groupedError && <Alert severity="error">{screenDrift.groupedError}</Alert>}
      {screenDrift.error && <Alert severity="error">{screenDrift.error}</Alert>}
      {!comparison ? (
        <EmptyState>Select a scenario and two versions to compare screen locator drift.</EmptyState>
      ) : (
        <Stack spacing={2}>
          <DriftSummary summary={comparison.summary || {}} />
          <Stack direction="row" spacing={2} sx={screenDriftTabStyles.filterRow}>
            <FormControlLabel
              control={
                <Switch
                  checked={screenDrift.showTestImpactOnly}
                  onChange={(event) =>
                    dispatch(setScreenDriftShowTestImpactOnly(event.target.checked))
                  }
                />
              }
              label="Show only test-impacting elements"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={screenDrift.showIgnored}
                  onChange={(event) => dispatch(setScreenDriftShowIgnored(event.target.checked))}
                />
              }
              label="Show ignored"
            />
          </Stack>
          {comparison.warnings?.map((warning) => (
            <Alert severity="warning" key={warning}>
              {warning}
            </Alert>
          ))}
          {filteredCategories.every((category) => category.items.length === 0) ? (
            <EmptyState>No drift items match the current filters.</EmptyState>
          ) : (
            filteredCategories
              .filter((category) => category.items.length > 0)
              .map((category) => (
                <DriftTable
                  key={category.key}
                  category={category}
                  allItems={items}
                  expandedIndex={expandedIndex}
                  setExpandedIndex={setExpandedIndex}
                  ignoreList={ignoreList}
                  onIgnore={(item) =>
                    dispatch(
                      addIgnoredLocatorAsync({
                        by: item.locator_by || item.by,
                        value: item.locator_value || item.value,
                      }),
                    )
                  }
                />
              ))
          )}
        </Stack>
      )}
    </Stack>
  );
}
